import axios from 'axios';
import Swal from 'sweetalert2';

const CANCEL_CONFIRMATION_WORD = 'ANNULÉ';
const DELETE_BLOCKED_MESSAGE = "Veuillez contacter Mr Issam pour qu'il contacte l'équipe IT pour la suppression de ce dossier.";

const escapeHtml = (value) => String(value || '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

export const isDossierCancelable = (dossier) => {
  if (!dossier || dossier.isDeleted || dossier.isCanceled) return false;

  const statusValues = [dossier.etatDossier, dossier.statut]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());

  return !statusValues.some((status) => (
    status === 'annulé' ||
    status === 'annule' ||
    status === 'annulée' ||
    status === 'annulee'
  ));
};

export const cancelDossierWithConfirmation = async ({
  clientId,
  dossierId,
  dossierLabel,
  getApiUrl,
  getToken,
  onSuccess
}) => {
  const safeDossierLabel = escapeHtml(dossierLabel || 'ce dossier');

  const { value: formValues, isConfirmed } = await Swal.fire({
    title: '<span style="color:#991b1b; font-weight:800;">Annulation du dossier</span>',
    html: `
      <div style="text-align:left;">
        <div style="display:flex; gap:14px; align-items:flex-start; padding:16px; border:1px solid #fecaca; background:linear-gradient(135deg,#fff7f7 0%,#fff1f2 100%); border-radius:14px; margin-bottom:18px;">
          <div style="width:42px; height:42px; min-width:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:#fee2e2; color:#b91c1c; font-size:24px; font-weight:900;">!</div>
          <div>
            <div style="font-size:15px; font-weight:800; color:#991b1b; margin-bottom:6px;">Action importante</div>
            <div style="font-size:13px; line-height:1.55; color:#7f1d1d;">
              Vous allez annuler <strong>${safeDossierLabel}</strong>. Cette action va libérer les stocks affectés, mettre à jour le prospect et conserver les paiements existants dans l'historique.
            </div>
          </div>
        </div>

        <div style="display:grid; gap:14px;">
          <div>
            <label for="dossier-cancel-reason" style="display:block; margin-bottom:7px; font-size:13px; font-weight:800; color:#374151;">
              Motif de l'annulation <span style="color:#dc2626;">*</span>
            </label>
            <textarea
              id="dossier-cancel-reason"
              class="swal2-textarea"
              rows="4"
              style="margin:0; width:100%; min-height:108px; box-sizing:border-box; border-radius:12px; border:1.5px solid #fca5a5; font-size:14px; line-height:1.5; box-shadow:none;"
              placeholder="Ex: demande client, erreur de saisie, dossier remplacé..."
            ></textarea>
          </div>

          <div style="padding:14px; border:1px dashed #ef4444; border-radius:12px; background:#fff7ed;">
            <label for="dossier-cancel-confirm" style="display:block; margin-bottom:7px; font-size:13px; font-weight:800; color:#7c2d12;">
              Saisir exactement <span style="display:inline-block; padding:2px 8px; border-radius:999px; background:#7f1d1d; color:#fff; letter-spacing:0.5px;">${CANCEL_CONFIRMATION_WORD}</span> pour confirmer <span style="color:#dc2626;">*</span>
            </label>
            <input
              id="dossier-cancel-confirm"
              class="swal2-input"
              style="margin:0; width:100%; height:46px; box-sizing:border-box; border-radius:12px; border:1.5px solid #fb923c; font-size:14px; box-shadow:none;"
              placeholder="${CANCEL_CONFIRMATION_WORD}"
              autocomplete="off"
            />
          </div>
        </div>
      </div>
    `,
    width: 620,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#64748b',
    confirmButtonText: 'Annuler définitivement',
    cancelButtonText: 'Annuler',
    focusCancel: true,
    reverseButtons: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    preConfirm: () => {
      const reason = document.getElementById('dossier-cancel-reason')?.value.trim() || '';
      const confirmWord = document.getElementById('dossier-cancel-confirm')?.value.trim() || '';

      if (!reason) {
        Swal.showValidationMessage("Le motif de l'annulation est obligatoire.");
        return false;
      }

      if (confirmWord !== CANCEL_CONFIRMATION_WORD) {
        Swal.showValidationMessage(`Vous devez saisir exactement ${CANCEL_CONFIRMATION_WORD} pour confirmer.`);
        return false;
      }

      return { cancelReason: reason };
    },
    didOpen: () => {
      const popup = Swal.getPopup();
      if (popup) {
        popup.style.borderRadius = '20px';
        popup.style.boxShadow = '0 30px 80px rgba(15, 23, 42, 0.28)';
      }
    }
  });

  if (!isConfirmed || !formValues) return false;

  try {
    const response = await axios.put(
      getApiUrl(`/clients/${clientId}/dossiers/${dossierId}/cancel`),
      { cancelReason: formValues.cancelReason },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );

    if (response.data?.success) {
      await Swal.fire({
        icon: 'success',
        title: 'Dossier annulé',
        text: 'Le dossier a été annulé, le stock a été libéré et le prospect a été mis à jour.',
        confirmButtonColor: '#1e3a5f'
      });

      await onSuccess?.(response);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Erreur lors de l'annulation:", error);
    await Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: error.response?.data?.message || 'Erreur lors de l\'annulation du dossier.',
      confirmButtonColor: '#1e3a5f'
    });
    return false;
  }
};

export const showDossierDeleteBlockedNotice = async () => {
  await Swal.fire({
    title: '<span style="color:#991b1b; font-weight:800;">Suppression du dossier bloquée</span>',
    html: `
      <div style="text-align:left;">
        <div style="display:flex; gap:14px; align-items:flex-start; padding:18px; border:1px solid #fecaca; background:linear-gradient(135deg,#fff7f7 0%,#fff1f2 100%); border-radius:16px; margin-bottom:16px;">
          <div style="width:46px; height:46px; min-width:46px; border-radius:14px; display:flex; align-items:center; justify-content:center; background:#fee2e2; color:#b91c1c; font-size:25px; font-weight:900;">!</div>
          <div>
            <div style="font-size:15px; font-weight:800; color:#991b1b; margin-bottom:6px;">Action sensible</div>
            <div style="font-size:13px; line-height:1.6; color:#7f1d1d;">
              La suppression directe d'un dossier est désactivée afin de préserver l'historique et les données financières.
            </div>
          </div>
        </div>
        <div style="padding:16px; border-radius:14px; background:#f8fafc; border:1px solid #e2e8f0; color:#1f2937; font-size:14px; line-height:1.6; font-weight:650;">
          ${escapeHtml(DELETE_BLOCKED_MESSAGE)}
        </div>
      </div>
    `,
    width: 560,
    icon: 'warning',
    showCloseButton: true,
    confirmButtonText: 'Compris',
    confirmButtonColor: '#1e3a5f',
    allowOutsideClick: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      if (popup) {
        popup.style.borderRadius = '20px';
        popup.style.boxShadow = '0 30px 80px rgba(15, 23, 42, 0.25)';
      }
    }
  });
};
